import { useState } from 'react';
import { Square, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useCurrentTimer, useDiscardTimer, useStopTimer } from '../queries';
import { TimerElapsed } from './TimerElapsed';
import { TimerNoteDialog } from './TimerNoteDialog';

export { TimerElapsed } from './TimerElapsed';

/**
 * Persistent running-timer pill in the header (ADR-076). Hidden when no timer is
 * running. Ticks client-side from `started_at`; the server is the source of truth.
 */
export function TimerIndicator() {
  const { data: timer } = useCurrentTimer();
  const stop = useStopTimer();
  const discard = useDiscardTimer();
  // The timer the note is being asked for — a later timer (this one stopped in
  // another tab, then a new one started) must not open with the dialog up.
  const [askingFor, setAskingFor] = useState<number | null>(null);

  if (!timer) return null;

  return (
    <div className="bg-primary/10 text-foreground flex items-center gap-1.5 rounded-md py-1 pr-1 pl-2 text-sm">
      <span className="relative flex size-2 shrink-0" aria-hidden>
        <span className="bg-primary absolute inline-flex size-full animate-ping rounded-full opacity-60" />
        <span className="bg-primary relative inline-flex size-2 rounded-full" />
      </span>
      <TimerElapsed startedAt={timer.started_at} />
      {timer.work_item_title ? (
        timer.project_public_id && timer.work_item_public_id ? (
          <Link
            to={`/projects/${timer.project_public_id}/work-items/${timer.work_item_public_id}`}
            className="text-muted-foreground hover:text-foreground hidden max-w-[10rem] truncate underline-offset-2 hover:underline md:inline"
            title={`Open ${timer.work_item_title}`}
          >
            {timer.work_item_title}
          </Link>
        ) : (
          <span
            className="text-muted-foreground hidden max-w-[10rem] truncate md:inline"
            title={timer.work_item_title}
          >
            {timer.work_item_title}
          </span>
        )
      ) : null}
      <Button
        size="icon"
        variant="ghost"
        className="size-7"
        disabled={stop.isPending}
        onClick={() => {
          stop.reset();
          setAskingFor(timer.id);
        }}
        aria-label="Stop timer and log time"
        title="Stop & log"
      >
        <Square className="size-3.5 fill-current" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="text-muted-foreground size-7"
        disabled={discard.isPending}
        onClick={() => discard.mutate()}
        aria-label="Discard timer without logging"
        title="Discard"
      >
        <X className="size-3.5" />
      </Button>
      <TimerNoteDialog
        open={askingFor === timer.id}
        onOpenChange={(next) => setAskingFor(next ? timer.id : null)}
        timer={timer}
        pending={stop.isPending}
        error={stop.error}
        onConfirm={(note) =>
          stop.mutate({ description: note }, { onSuccess: () => setAskingFor(null) })
        }
      />
    </div>
  );
}
